import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import './BannerCarousel.css';

export interface BannerSlide {
  id: string;
  imageSrc: string;
  imageAlt: string;
  fit?: 'cover' | 'contain';
}

interface BannerCarouselProps {
  slides: readonly BannerSlide[];
}

const BannerCarousel: React.FC<BannerCarouselProps> = ({ slides }) => {
  const { t } = useTranslation();
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);

  if (slides.length === 0) {
    return null;
  }

  const showPreviousSlide = () => {
    setActiveSlideIndex((currentIndex) =>
      currentIndex === 0 ? slides.length - 1 : currentIndex - 1,
    );
  };

  const showNextSlide = () => {
    setActiveSlideIndex((currentIndex) =>
      currentIndex === slides.length - 1 ? 0 : currentIndex + 1,
    );
  };

  return (
    <section
      className="banner-carousel"
      aria-roledescription="carousel"
      aria-label={t('home.banner_carousel_label')}
    >
      <div
        className="banner-carousel__track"
        style={{ transform: `translateX(-${activeSlideIndex * 100}%)` }}
      >
        {slides.map((slide, slideIndex) => (
          <div
            className="banner-carousel__slide"
            key={slide.id}
            aria-hidden={slideIndex !== activeSlideIndex}
          >
            <img
              className={`banner-carousel__image banner-carousel__image--${slide.fit ?? 'cover'}`}
              src={slide.imageSrc}
              alt={slide.imageAlt}
              draggable={false}
            />
          </div>
        ))}
      </div>

      {slides.length > 1 && (
        <>
          <button
            className="banner-carousel__arrow banner-carousel__arrow--previous"
            type="button"
            onClick={showPreviousSlide}
            aria-label={t('home.previous_banner')}
          >
            <ChevronLeft aria-hidden="true" />
          </button>
          <button
            className="banner-carousel__arrow banner-carousel__arrow--next"
            type="button"
            onClick={showNextSlide}
            aria-label={t('home.next_banner')}
          >
            <ChevronRight aria-hidden="true" />
          </button>

          <div className="banner-carousel__indicators" aria-label={t('home.choose_banner')}>
            {slides.map((slide, slideIndex) => (
              <button
                className={`banner-carousel__indicator${
                  slideIndex === activeSlideIndex ? ' banner-carousel__indicator--active' : ''
                }`}
                key={slide.id}
                type="button"
                onClick={() => setActiveSlideIndex(slideIndex)}
                aria-label={t('home.show_banner', { number: slideIndex + 1 })}
                aria-current={slideIndex === activeSlideIndex ? 'true' : undefined}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
};

export default BannerCarousel;
